import { useCallback, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, SaveButton } from '@/shared/ui';
import { useAgendaParams } from '@/shared/navigation/react/useAgendaParams';
import { useCloseGuard } from '@/shared/navigation/react';
import { useAutoSave } from '@/shared/hooks';
import type { ColumbusPlannerTermModel } from '@/api/generated/schemas';
import {
  useLoadColumbusPlanner,
  useSaveColumbusTimeLine,
  useSaveColumbusPlannerSettings,
} from './columbusQueries';
import { columbusDetailAgenda } from './columbusDetailAgenda';
import { useColumbusStore, computeColorCbId } from './columbusStore';
import { PublishButton } from './components/PublishButton';
import { TimeLineSlider } from './flights/components/TimeLineSlider';
import { TermsGroup } from './terms/components/TermsGroup';

const AUTO_SAVE_DELAY_MS = 5000;

interface ColumbusDetailPageProps {
  layerId: string;
}

interface TermGroup {
  colorCbId: number | null;
  terms: ColumbusPlannerTermModel[];
}

export function ColumbusDetailPage({ layerId }: ColumbusDetailPageProps) {
  const { t } = useTranslation('columbus-planner');
  const { open } = useAgendaParams(columbusDetailAgenda, layerId);
  const { productId, termsSerialId } = open;

  const {
    dateFrom,
    dateTo,
    segments,
    plans,
    terms,
    calculations,
    groupCosts,
    paxCosts,
    maxTariffsCount,
    timelineRevision,
    planRevisions,
    initData,
    addSegment,
    removeSegment,
    updateSegmentColor,
    setAllSegments,
    updateTariff,
    addTariff,
    removeTariff,
    addGroupCost,
    updateGroupCostName,
    updateGroupCostPrice,
    removeGroupCost,
    addPaxCost,
    updatePaxCostName,
    updatePaxCostPrice,
    removePaxCost,
    updateTicketPrice,
  } = useColumbusStore();

  const { data, isLoading, isError, error } = useLoadColumbusPlanner(productId, termsSerialId);

  // ── Dirty tracking ──────────────────────────────────────────────────────────
  const savedTimelineRevRef = useRef(0);
  const savedPlanRevsRef = useRef<Record<string, number>>({});

  useEffect(() => {
    if (data) {
      initData(data);
      savedTimelineRevRef.current = 0;
      savedPlanRevsRef.current = {};
    }
  }, [data, initData]);

  const hasChanges =
    timelineRevision > savedTimelineRevRef.current ||
    Object.entries(planRevisions).some(([k, rev]) => rev > (savedPlanRevsRef.current[k] ?? 0));

  const changeCount =
    (timelineRevision > savedTimelineRevRef.current ? 1 : 0) +
    Object.entries(planRevisions).filter(([k, rev]) => rev > (savedPlanRevsRef.current[k] ?? 0))
      .length;

  // Součet všech revizí – změní se při každé mutaci, resetuje debounce timer
  const totalRevision = timelineRevision + Object.values(planRevisions).reduce((a, b) => a + b, 0);

  // ── Mutations ───────────────────────────────────────────────────────────────
  const saveTimeLineMutation = useSaveColumbusTimeLine();
  const saveSettingsMutation = useSaveColumbusPlannerSettings(productId, termsSerialId);

  const routeRef = useRef({ productId, termsSerialId });
  routeRef.current = { productId, termsSerialId };

  // ── Sekvenční save queue (čistá práce, bez reentrancy guardu) ───────────────
  const flushDirtyItems = useCallback(async () => {
    while (true) {
      const {
        timelineRevision: tlRev,
        planRevisions: planRevs,
        dateFrom: df,
        dateTo: dt,
        segments: segs,
        timeLineId: storeTlId,
        plans: currentPlans,
      } = useColumbusStore.getState();

      const { productId: pid, termsSerialId: tsid } = routeRef.current;

      type DirtyItem = { key: 'timeline' | string; rev: number };
      const dirtyItems: DirtyItem[] = [];

      const dirtyPlanKeys = Object.entries(planRevs)
        .filter(([k, rev]) => rev > (savedPlanRevsRef.current[k] ?? 0))
        .map(([k, rev]) => ({ key: k, rev }));

      // Timeline musí existovat dřív, než lze uložit jakékoli nastavení plánu.
      // Pokud ještě nevznikla (storeTlId == null) a jsou pending plan changes,
      // vynuť její vytvoření i bez explicitní změny timeline revize.
      const needTimelineForPlans = storeTlId == null && dirtyPlanKeys.length > 0;

      if (tlRev > savedTimelineRevRef.current || needTimelineForPlans) {
        dirtyItems.push({ key: 'timeline', rev: tlRev });
      }
      dirtyItems.push(...dirtyPlanKeys);

      if (dirtyItems.length === 0) break;

      // Lokální mutable reference – updatuje se po vytvoření timeline,
      // aby následné položky ve stejné iteraci znaly nové ID.
      let tlId = storeTlId;
      let savedCount = 0;

      for (const { key, rev } of dirtyItems) {
        try {
          if (key === 'timeline') {
            if (df && dt) {
              const response = await saveTimeLineMutation.mutateAsync({
                data: {
                  productId: pid,
                  termsSerialId: tsid,
                  dateFrom: df,
                  dateTo: dt,
                  segments: segs,
                },
              });
              if (tlId == null) {
                tlId = response.calculationTimeLineId;
                useColumbusStore.setState({ timeLineId: response.calculationTimeLineId });
              }
              if (savedTimelineRevRef.current < rev) savedTimelineRevRef.current = rev;
              savedCount++;
            }
          } else if (tlId != null) {
            const plan = currentPlans[key];
            if (plan) {
              const termMatch = /^term:(\d+)$/.exec(key);
              const colorMatch = /^color:(\d+)$/.exec(key);
              await saveSettingsMutation.mutateAsync({
                data: {
                  calculationTimeLineId: tlId,
                  termId: termMatch ? Number(termMatch[1]) : null,
                  colorCbId: colorMatch ? Number(colorMatch[1]) : null,
                  planData: plan,
                },
              });
              if ((savedPlanRevsRef.current[key] ?? 0) < rev) savedPlanRevsRef.current[key] = rev;
              savedCount++;
            }
          }
        } catch {
          // položka zůstane dirty, zkusí se znovu při dalším průchodu
        }
      }

      if (savedCount === 0) break;
    }
  }, [saveTimeLineMutation, saveSettingsMutation]);

  const { isSaving, triggerSave } = useAutoSave({
    flush: flushDirtyItems,
    isDirty: hasChanges,
    changeToken: totalRevision,
    delayMs: AUTO_SAVE_DELAY_MS,
  });

  const { dialogNode } = useCloseGuard(layerId, hasChanges || isSaving, {
    title: t('guard.title'),
    message: t('guard.message'),
    confirmLabel: t('guard.confirm'),
    cancelLabel: t('guard.cancel'),
  });

  const termGroups = useMemo<TermGroup[]>(() => {
    const map = new Map<string, TermGroup>();
    for (const term of terms) {
      const colorCbId = computeColorCbId(term.dateFrom, dateFrom, segments);
      const key = colorCbId != null ? `color:${colorCbId}` : 'default';
      if (!map.has(key)) {
        map.set(key, { colorCbId, terms: [] });
      }
      map.get(key)!.terms.push(term);
    }
    return Array.from(map.values());
  }, [terms, dateFrom, segments]);

  if (isLoading) {
    return <div className="text-muted-foreground p-4 text-sm">{t('states.loading')}</div>;
  }

  if (isError) {
    return (
      <div className="p-4">
        <Alert severity="error" title={t('states.errorTitle')}>
          {error instanceof Error ? error.message : t('states.errorDescription')}
        </Alert>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header – vždy plná šířka viditelné oblasti, nesrolluje horizontálně */}
      <div className="border-border bg-phx-surface flex flex-none items-center justify-between gap-2 border-b px-4 py-2 shadow-sm">
        <h2 className="text-lg font-semibold">{t('detail.title')}</h2>
        <div className="flex items-center gap-2">
          <SaveButton
            onClick={() => void triggerSave()}
            hasChanges={hasChanges}
            isSaving={isSaving}
            changeCount={changeCount}
          />
          <PublishButton
            productId={productId}
            destinationSeasonId={open.destinationSeasonId}
            termsSerialId={termsSerialId}
            disabled={isSaving || hasChanges}
          />
        </div>
      </div>

      {/* Scroll oblast – vertikální i horizontální, scrollbar vždy dole u okraje okna */}
      <div className="min-h-0 flex-1 overflow-auto">
        <div className="bg-phx-surface min-w-max space-y-6 p-4">
          <TimeLineSlider
            dateFrom={dateFrom}
            dateTo={dateTo}
            segments={segments}
            onAddSegment={addSegment}
            onRemoveSegment={removeSegment}
            onChangeColor={updateSegmentColor}
            onSetAllSegments={setAllSegments}
          />

          {terms.length === 0 ? (
            <p className="text-muted-foreground px-1 text-sm">{t('detail.noTerms')}</p>
          ) : (
            termGroups.map((group) => (
              <TermsGroup
                key={group.colorCbId ?? 'default'}
                colorCbId={group.colorCbId}
                initialExpandedTermId={open.termId}
                terms={group.terms}
                plans={plans}
                calculations={calculations}
                groupCostNames={groupCosts}
                paxCostNames={paxCosts}
                maxTariffsCount={maxTariffsCount}
                productId={productId}
                destinationSeasonId={open.destinationSeasonId}
                termsSerialId={termsSerialId}
                onUpdateTariff={updateTariff}
                onAddTariff={addTariff}
                onRemoveTariff={removeTariff}
                onUpdateGroupCostName={updateGroupCostName}
                onUpdateGroupCostPrice={updateGroupCostPrice}
                onAddGroupCost={addGroupCost}
                onRemoveGroupCost={removeGroupCost}
                onUpdatePaxCostName={updatePaxCostName}
                onUpdatePaxCostPrice={updatePaxCostPrice}
                onAddPaxCost={addPaxCost}
                onRemovePaxCost={removePaxCost}
                onUpdateTicketPrice={updateTicketPrice}
              />
            ))
          )}
        </div>
      </div>

      {dialogNode}
    </div>
  );
}
